import React, { useState } from "react";

import * as api from "./api";

/**
 * Delete a creative session — the operator-facing half of SESSION-LIFECYCLE.
 *
 * FOUNDER RULINGS, 2026-09-09:
 *   · Hard delete, immediately. No soft-delete grace window, so the
 *     confirmation is the only thing between a click and a gone session — which
 *     is why it says what will happen rather than just asking twice.
 *   · Only the session's creator or the Founder may delete. The backend is the
 *     authority; this screen renders its refusal rather than guessing at
 *     permissions it cannot see.
 *   · One track of a multi-angle set cannot be deleted alone. The backend
 *     answers 409 with the arithmetic, and this offers "delete all N" as the
 *     explicit alternative — never automatically, and never by renumbering.
 *
 * WHAT THE CONFIRMATION SAYS, AND WHY. Deleting a session does NOT delete its
 * angles, hooks, scripts or CTAs — those are keyed by OFFER and survive.
 * Demonstrated the same day this was built: removing 11 orphaned sessions left
 * 173 artifacts belonging to their deleted offers still in the database. An
 * operator who believes they are destroying creative work will hesitate over
 * the wrong thing, so the dialog says plainly what goes and what stays.
 *
 * NO `window.confirm`. A native dialog blocks the whole page and cannot say any
 * of the above.
 */
export default function SessionDelete({ session, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [setRefusal, setSetRefusal] = useState(null);   // the 409 text

  const label = `Session ${session.session_number ?? session.session_id}`;
  const trackTotal = Number(session.track_total || 0);
  const isTrack = trackTotal > 1;

  const run = async (cascade) => {
    setBusy(true);
    setError(null);
    const res = await api.deleteCreativeSession(session.session_id, { cascade });
    setBusy(false);

    if (res.ok) {
      setOpen(false);
      setSetRefusal(null);
      if (onDeleted) onDeleted(res.data);
      return;
    }

    // THREE REFUSALS, THREE BEHAVIOURS. A single "failed" would leave the
    // operator with no idea whether to ask someone, try again, or do something
    // different.
    if (res.status === 409) {
      // It is one track of a set. Offer the whole-set action, deliberately as a
      // second, separate decision.
      setSetRefusal(res.error);
    } else {
      setError(res.error || "The session could not be deleted.");
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="btn-ghost btn-sm ws-session-delete"
        onClick={() => { setOpen(true); setError(null); setSetRefusal(null); }}
        data-testid={`ws-session-delete-${session.session_id}`}
        title="Delete this session"
      >
        Delete
      </button>
    );
  }

  return (
    <div className="ws-session-delete-confirm" role="group"
         aria-label={`Delete ${label}`}
         data-testid={`ws-session-delete-confirm-${session.session_id}`}>
      {setRefusal ? (
        <>
          <p className="ws-step-note state-warn" role="alert"
             data-testid={`ws-session-delete-set-${session.session_id}`}>
            {setRefusal}
          </p>
          <div className="ws-session-delete-actions">
            <button type="button" className="btn-ghost btn-sm"
                    onClick={() => { setOpen(false); setSetRefusal(null); }}
                    data-testid={`ws-session-delete-cancel-${session.session_id}`}>
              Keep it
            </button>
            <button type="button" className="btn-danger btn-sm"
                    onClick={() => run(true)} disabled={busy}
                    data-testid={`ws-session-delete-cascade-${session.session_id}`}>
              {busy ? "Deleting…" : `Delete all ${trackTotal} tracks`}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="ws-step-note">
            Delete {label}?{" "}
            {isTrack ? (
              <>It is track {session.track_index} of {trackTotal} in a
              multi-angle set. </>
            ) : null}
            <span className="dim">
              This cannot be undone. The session goes; its angles, hooks,
              scripts and CTAs belong to the offer and stay where they are.
            </span>
          </p>
          {error ? (
            <p className="ws-step-note state-error" role="alert"
               data-testid={`ws-session-delete-error-${session.session_id}`}>
              {error}
            </p>
          ) : null}
          <div className="ws-session-delete-actions">
            <button type="button" className="btn-ghost btn-sm"
                    onClick={() => setOpen(false)}
                    data-testid={`ws-session-delete-cancel-${session.session_id}`}>
              Keep it
            </button>
            <button type="button" className="btn-danger btn-sm"
                    onClick={() => run(false)} disabled={busy}
                    data-testid={`ws-session-delete-confirm-btn-${session.session_id}`}>
              {busy ? "Deleting…" : "Delete session"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
